import ProductSearch from "../components/ProductSearch";
import { useState, useEffect, useCallback } from "react";
import PaginationBar from "../components/PaginationBar";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang, getLocale } from "../lang/useLanguage";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { createPortal } from "react-dom";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const hdr = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const money = (v) => v != null ? `${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺` : "—";
const kg = (v) => v != null ? `${Number(v).toFixed(2)} kg` : "—";

const getRecoveryLabels = () => {
  const l = getLang(); const s = l.scrap || {};
  return {
    hurdaci_sat:    s.recHurdaciSat,
    yeniden_isle:   s.recYenidenIsle,
    geri_hammadde:  s.recGeriHammadde,
    lisanli_imha:   s.recLisanliImha,
    beklemede:      s.recBeklemede,
  };
};
const RECOVERY_COLORS = { hurdaci_sat: "#22c55e", yeniden_isle: "#60a5fa", geri_hammadde: "#a78bfa", lisanli_imha: "#ef4444", beklemede: "#f97316" };

const getStatusLabels = () => {
  const l = getLang(); const s = l.scrap || {};
  return {
    beklemede:   s.statusBeklemede,
    isleniyor:   s.statusIsleniyor,
    tamamlandi:  s.statusTamamlandi,
  };
};
const STATUS_COLORS = { beklemede: "#f97316", isleniyor: "#60a5fa", tamamlandi: "#22c55e" };

const getToolTypes = () => {
  const l = getLang(); const s = l.scrap || {};
  return {
    elmas_uc:      s.toolElmasUc,
    freze:         s.toolFreze,
    matkap:        s.toolMatkap,
    taslama_tasi:  s.toolTaslamaTasi,
    diger:         s.toolDiger,
  };
};

const getFluidTypes = () => {
  const l = getLang(); const s = l.scrap || {};
  return {
    hidrolik_yag:  s.fluidHidrolik,
    kesme_sivisi:  s.fluidKesme,
    sogutma_suyu:  s.fluidSogutma,
    motor_yagi:    s.fluidMotor,
    diger:         s.fluidDiger,
  };
};

const getMatCatLabels = () => {
  const l = getLang(); const s = l.scrap || {};
  return {
    metal:    s.catMetal,
    plastik:  s.catPlastik,
    yag:      s.catYag,
    takim:    s.catTakim,
    diger:    s.catDiger,
  };
};

const MAT_CAT_COLORS = ["#60a5fa", "#a78bfa", "#fbbf24", "#f97316", "#6b7280"];

const Badge = ({ label, color }) => (
  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color, background: `${color}18`, border: `1px solid ${color}44` }}>{label}</span>
);

const KpiCard = ({ label, value, icon, color, sub, t }) => (
  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: color || t.accent.blue }}>{value ?? "—"}</div>
        {sub && <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      <span style={{ fontSize: 26, opacity: 0.7 }}>{icon}</span>
    </div>
  </div>
);

// ─── HURDA KAYIT FORMU ────────────────────────────────────────────────────
const ScrapFormModal = ({ record, materialTypes, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const s = l.scrap || {};
  const isAuto = record?.auto_generated;
  const empty = {
    product_code: "", product_name: "", work_order_no: "",
    scrap_date: new Date().toISOString().slice(0, 10),
    quantity: 1, unit: "kg", scrap_reason: "", scrap_category: "diger",
    weight_kg: "", unit_price_per_kg: "",
    recovery_method: "beklemede", recovery_company: "", recovery_date: "",
    recovery_amount: "", notes: "", status: "beklemede",
    source_type: "manual", scrap_material_type_id: "",
  };
  const [form, setForm] = useState(record ? { ...empty, ...record } : empty);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const calcTotal = () => (parseFloat(form.weight_kg || 0) * parseFloat(form.unit_price_per_kg || 0)).toFixed(2);

  const handleMaterialSelect = (id) => {
    set("scrap_material_type_id", id);
    const mt = materialTypes.find(m => m.id === parseInt(id));
    if (mt && !form.unit_price_per_kg) set("unit_price_per_kg", mt.avg_price_per_unit);
  };

  const handleSave = async () => {
    if (!form.product_code) return setError(s.errProductCode);
    if (!form.weight_kg || parseFloat(form.weight_kg) <= 0) return setError(s.errWeight);
    setLoading(true); setError("");
    try {
      const url = record ? `${API}/scrap/records/${record.id}` : `${API}/scrap/records`;
      const res = await fetch(url, { method: record ? "PUT" : "POST", headers: hdr(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || (s.errSave));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const inpDisabled = { ...inp, opacity: 0.6, cursor: "not-allowed", background: t.bg.toggle };
  const sel = { ...inp, cursor: "pointer" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;
  const Sec = ({ children }) => <div style={{ fontSize: 11, fontWeight: 700, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 4 }}>{children}</div>;
  const grouped = materialTypes.reduce((acc, m) => { if (!acc[m.category]) acc[m.category] = []; acc[m.category].push(m); return acc; }, {});
  const MAT_CAT_LABELS = getMatCatLabels();
  const RECOVERY_LABELS = getRecoveryLabels();
  const STATUS_LABELS = getStatusLabels();

  return createPortal(
    (
    <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 700, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: "linear-gradient(135deg,#f9731622,#ef444411)", borderBottom: "1px solid #f9731633", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
              {record ? `✏️ ${s.editRecord}` : `➕ ${s.newRecord}`}
            </div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>
              {isAuto ? `🤖 ${s.autoNote}` : (s.formSubtitle)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ padding: 28, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {isAuto && (
            <div style={{ padding: "10px 14px", background: "#f9731611", border: "1px solid #f9731633", borderRadius: 8, fontSize: 12, color: "#f97316" }}>
              🤖 {s.autoLockNote}
            </div>
          )}

          <Sec>📋 {s.secSource}</Sec>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><Lbl>{s.sourceType}</Lbl>
              <select value={form.source_type} onChange={e => set("source_type", e.target.value)} style={sel} disabled={isAuto}>
                <option value="manual">{s.sourceManual}</option>
                <option value="work_order">{s.sourceWorkOrder}</option>
                <option value="kk_reject">{s.sourceKkReject}</option>
                <option value="genel">{s.sourceGeneral}</option>
              </select>
            </div>
            <div><Lbl>{s.productCode} *</Lbl>
              {isAuto
                ? <div style={inpDisabled}>{form.product_code}</div>
                : <ProductSearch value={form.product_code} onChange={(code, name) => { set("product_code", code); if (name) set("product_name", name); }} placeholder={s.productSearchPlaceholder} t={t} />
              }
            </div>
            <div><Lbl>{s.workOrderNo}</Lbl>
              {isAuto
                ? <div style={inpDisabled}>{form.work_order_no || "—"}</div>
                : <input value={form.work_order_no} onChange={e => set("work_order_no", e.target.value)} placeholder="IE-2026-0001" style={inp} />
              }
            </div>
          </div>

          <Sec>🔩 {s.secMaterial}</Sec>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><Lbl>{s.materialType}</Lbl>
              <select value={form.scrap_material_type_id} onChange={e => handleMaterialSelect(e.target.value)} style={sel}>
                <option value="">— {s.select} —</option>
                {Object.entries(grouped).map(([cat, items]) => (
                  <optgroup key={cat} label={MAT_CAT_LABELS[cat] || cat}>
                    {items.map(m => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div><Lbl>{s.scrapCategory}</Lbl>
              <select value={form.scrap_category} onChange={e => set("scrap_category", e.target.value)} style={sel}>
                <option value="kalite_red">{s.catQualityReject}</option>
                <option value="makine_ariza">{s.catMachineFault}</option>
                <option value="malzeme_hatasi">{s.catMaterialError}</option>
                <option value="operator_hatasi">{s.catOperatorError}</option>
                <option value="diger_karisik">{s.catMixed}</option>
                <option value="diger">{s.catOther}</option>
              </select>
            </div>
          </div>

          <Sec>⚖️ {s.secQtyValue}</Sec>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
            <div><Lbl>{s.date} *</Lbl><input type="date" value={form.scrap_date} onChange={e => set("scrap_date", e.target.value)} style={inp} /></div>
            <div><Lbl>{s.weightKg} *</Lbl><input type="number" min="0" step="0.001" value={form.weight_kg} onChange={e => set("weight_kg", e.target.value)} placeholder="0.000" style={inp} /></div>
            <div><Lbl>₺ / kg</Lbl><input type="number" min="0" step="0.01" value={form.unit_price_per_kg} onChange={e => set("unit_price_per_kg", e.target.value)} placeholder="0.00" style={inp} /></div>
            <div><Lbl>{s.totalValue}</Lbl><div style={{ ...inp, background: t.bg.toggle, color: "#22c55e", fontWeight: 700 }}>{calcTotal()} ₺</div></div>
          </div>
          <div><Lbl>{s.description_}</Lbl><textarea value={form.scrap_reason} onChange={e => set("scrap_reason", e.target.value)} placeholder={s.scrapReasonPlaceholder} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>

          <Sec>♻️ {s.secRecovery}</Sec>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div><Lbl>{s.method}</Lbl>
              <select value={form.recovery_method} onChange={e => set("recovery_method", e.target.value)} style={sel}>
                {Object.entries(RECOVERY_LABELS).map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
              </select>
            </div>
            <div><Lbl>{s.company}</Lbl><input value={form.recovery_company} onChange={e => set("recovery_company", e.target.value)} placeholder={s.companyPlaceholder} style={inp} /></div>
            <div><Lbl>{s.date}</Lbl><input type="date" value={form.recovery_date || ""} onChange={e => set("recovery_date", e.target.value)} style={inp} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><Lbl>{s.recoveryAmount}</Lbl><input type="number" min="0" step="0.01" value={form.recovery_amount || ""} onChange={e => set("recovery_amount", e.target.value)} placeholder="0.00" style={inp} /></div>
            <div><Lbl>{s.statusLabel}</Lbl>
              <select value={form.status} onChange={e => set("status", e.target.value)} style={sel}>
                {Object.entries(STATUS_LABELS).map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
              </select>
            </div>
          </div>
          <div><Lbl>{s.notes}</Lbl><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>⚠️ {error}</div>}
        </div>

        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{s.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#f97316,#ef4444)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {loading ? (s.saving) : (record ? `💾 ${s.update}` : `➕ ${s.save}`)}
          </button>
        </div>
      </div>
    </div>
  ),
    document.body
  )
};

// ─── TAKIM FORMU ──────────────────────────────────────────────────────────
const ToolFormModal = ({ onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const s = l.scrap || {};
  const TOOL_TYPES = getToolTypes();
  const [form, setForm] = useState({ tool_name: "", tool_type: "elmas_uc", machine_name: "", quantity: 1, unit_price: "", scrap_date: new Date().toISOString().slice(0, 10), disposal_method: "beklemede", disposal_company: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const sel = { ...inp, cursor: "pointer" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;
  const handleSave = async () => {
    if (!form.tool_name) return setError(s.errToolName);
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/scrap/tools`, { method: "POST", headers: hdr(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || (s.errSave));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };
  return createPortal(
    (
    <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 560, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: "linear-gradient(135deg,#f9731622,#ef444411)", borderBottom: "1px solid #f9731633", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>🔧 {s.toolFormTitle}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><Lbl>{s.toolName} *</Lbl><input value={form.tool_name} onChange={e => set("tool_name", e.target.value)} placeholder="CNMG 120408 vb." style={inp} /></div>
            <div><Lbl>{s.toolType}</Lbl><select value={form.tool_type} onChange={e => set("tool_type", e.target.value)} style={sel}>{Object.entries(TOOL_TYPES).map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}</select></div>
            <div><Lbl>{s.machine_}</Lbl><input value={form.machine_name} onChange={e => set("machine_name", e.target.value)} placeholder="CNC-001" style={inp} /></div>
            <div><Lbl>{s.date}</Lbl><input type="date" value={form.scrap_date} onChange={e => set("scrap_date", e.target.value)} style={inp} /></div>
            <div><Lbl>{s.quantity_}</Lbl><input type="number" min="1" value={form.quantity} onChange={e => set("quantity", e.target.value)} style={inp} /></div>
            <div><Lbl>{s.unitPrice}</Lbl><input type="number" min="0" step="0.01" value={form.unit_price} onChange={e => set("unit_price", e.target.value)} placeholder="0.00" style={inp} /></div>
            <div><Lbl>{s.method}</Lbl>
              <select value={form.disposal_method} onChange={e => set("disposal_method", e.target.value)} style={sel}>
                <option value="beklemede">{s.recBeklemede}</option>
                <option value="hurdaci_sat">{s.recHurdaciSat}</option>
                <option value="imha">{s.recImha}</option>
              </select>
            </div>
            <div><Lbl>{s.company}</Lbl><input value={form.disposal_company} onChange={e => set("disposal_company", e.target.value)} style={inp} /></div>
          </div>
          <div><Lbl>{s.notes}</Lbl><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>⚠️ {error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{s.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#f97316,#ef4444)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {loading ? (s.saving) : `➕ ${s.save}`}
          </button>
        </div>
      </div>
    </div>
  ),
    document.body
  )
};

// ─── SIVI FORMU ───────────────────────────────────────────────────────────
const FluidFormModal = ({ onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const s = l.scrap || {};
  const FLUID_TYPES = getFluidTypes();
  const [form, setForm] = useState({ fluid_type: "hidrolik_yag", volume_lt: "", container_count: 1, container_type: "varil", collection_company: "", collection_date: "", sale_price: "", status: "beklemede", machine_name: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const sel = { ...inp, cursor: "pointer" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;
  const handleSave = async () => {
    if (!form.volume_lt || parseFloat(form.volume_lt) <= 0) return setError(s.errVolume);
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/scrap/fluids`, { method: "POST", headers: hdr(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || (s.errSave));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };
  return createPortal(
    (
    <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 560, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: "linear-gradient(135deg,#fbbf2422,#f9731611)", borderBottom: "1px solid #fbbf2433", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>🛢️ {s.fluidFormTitle}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><Lbl>{s.fluidType} *</Lbl><select value={form.fluid_type} onChange={e => set("fluid_type", e.target.value)} style={sel}>{Object.entries(FLUID_TYPES).map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}</select></div>
            <div><Lbl>{s.volumeLt} *</Lbl><input type="number" min="0" step="0.1" value={form.volume_lt} onChange={e => set("volume_lt", e.target.value)} placeholder="0.0" style={inp} /></div>
            <div><Lbl>{s.containerCount}</Lbl><input type="number" min="1" value={form.container_count} onChange={e => set("container_count", e.target.value)} style={inp} /></div>
            <div><Lbl>{s.containerType}</Lbl>
              <select value={form.container_type} onChange={e => set("container_type", e.target.value)} style={sel}>
                <option value="varil">{s.barrel}</option>
                <option value="bidon">{s.canister}</option>
                <option value="tank">{s.tank}</option>
                <option value="diger">{s.catOther}</option>
              </select>
            </div>
            <div><Lbl>{s.collectionCompany}</Lbl><input value={form.collection_company} onChange={e => set("collection_company", e.target.value)} style={inp} /></div>
            <div><Lbl>{s.collectionDate}</Lbl><input type="date" value={form.collection_date || ""} onChange={e => set("collection_date", e.target.value)} style={inp} /></div>
            <div><Lbl>{s.salePrice}</Lbl><input type="number" min="0" step="0.01" value={form.sale_price} onChange={e => set("sale_price", e.target.value)} placeholder="0.00" style={inp} /></div>
            <div><Lbl>{s.statusLabel}</Lbl>
              <select value={form.status} onChange={e => set("status", e.target.value)} style={sel}>
                <option value="beklemede">{s.statusBeklemede}</option>
                <option value="toplandi">{s.statusToplandi}</option>
                <option value="satildi">{s.statusSatildi}</option>
              </select>
            </div>
            <div><Lbl>{s.machine_}</Lbl><input value={form.machine_name} onChange={e => set("machine_name", e.target.value)} placeholder="CNC-001" style={inp} /></div>
          </div>
          <div><Lbl>{s.notes}</Lbl><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>⚠️ {error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{s.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#fbbf24,#f97316)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {loading ? (s.saving) : `➕ ${s.save}`}
          </button>
        </div>
      </div>
    </div>
  ),
    document.body
  )
};

// ─── TANIM FORMU ─────────────────────────────────────────────────────────
const DefinitionFormModal = ({ record, materialTypes, onClose, onSaved, t }) => {
  const { t: l } = useLanguage();
  const s = l.scrap || {};
  const [form, setForm] = useState(record ? {
    product_code: record.product_code || "",
    scrap_material_type_id: record.scrap_material_type_id || "",
    scrap_per_unit_kg: record.scrap_per_unit_kg || "",
    reject_weight_kg: record.reject_weight_kg || "",
    notes: record.notes || "",
  } : { product_code: "", scrap_material_type_id: "", scrap_per_unit_kg: "", reject_weight_kg: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = { width: "100%", padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const sel = { ...inp, cursor: "pointer" };
  const Lbl = ({ children }) => <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{children}</label>;
  const grouped = materialTypes.reduce((acc, m) => { if (!acc[m.category]) acc[m.category] = []; acc[m.category].push(m); return acc; }, {});
  const MAT_CAT_LABELS = getMatCatLabels();

  const handleSave = async () => {
    if (!form.product_code) return setError(s.errProductCode);
    if (!form.scrap_material_type_id) return setError(s.errMaterialType);
    setLoading(true); setError("");
    try {
      const url = record ? `${API}/scrap/definitions/${record.id}` : `${API}/scrap/definitions`;
      const method = record ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: hdr(), body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail || (s.errSave));
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return createPortal(
    (
    <div style={{ position: "fixed", inset: 0, zIndex: 100000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 560, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", background: "linear-gradient(135deg,#60a5fa22,#a78bfa11)", borderBottom: "1px solid #60a5fa33", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
              {record ? `✏️ ${s.editDef}` : `⚙️ ${s.newDef}`}
            </div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>{s.defSubtitle}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 14 }}>
          {!record && (
            <div style={{ padding: "12px 16px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 10, fontSize: 12, color: t.accent.blue }}>
              💡 {s.defHint}
            </div>
          )}
          <div><Lbl>{s.productCode} *</Lbl>
            {record
              ? <div style={{ ...inp, opacity: 0.7, background: t.bg.toggle }}>{form.product_code}</div>
              : <ProductSearch value={form.product_code} onChange={(code) => set("product_code", code)} placeholder={s.productSearchPlaceholder} t={t} />
            }
          </div>
          <div><Lbl>{s.materialType} *</Lbl>
            <select value={form.scrap_material_type_id} onChange={e => set("scrap_material_type_id", e.target.value)} style={sel}>
              <option value="">— {s.select} —</option>
              {Object.entries(grouped).map(([cat, items]) => (
                <optgroup key={cat} label={MAT_CAT_LABELS[cat] || cat}>
                  {items.map(m => <option key={m.id} value={m.id}>{m.name} — {m.avg_price_per_unit} ₺/{m.unit}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Lbl>{s.scrapPerUnit}</Lbl>
              <input type="number" min="0" step="0.001" value={form.scrap_per_unit_kg} onChange={e => set("scrap_per_unit_kg", e.target.value)} placeholder="0.500" style={inp} />
              <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{s.scrapPerUnitNote}</div>
            </div>
            <div>
              <Lbl>{s.rejectWeight}</Lbl>
              <input type="number" min="0" step="0.001" value={form.reject_weight_kg} onChange={e => set("reject_weight_kg", e.target.value)} placeholder="2.500" style={inp} />
              <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{s.rejectWeightNote}</div>
            </div>
          </div>
          <div><Lbl>{s.notes}</Lbl><textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} /></div>
          {error && <div style={{ padding: "10px 14px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>⚠️ {error}</div>}
        </div>
        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>{s.cancel}</button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#60a5fa,#a78bfa)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {loading ? (s.saving) : (record ? `💾 ${s.update}` : `💾 ${s.save}`)}
          </button>
        </div>
      </div>
    </div>
  ),
    document.body
  )
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function Scrap() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const s = l.scrap || {};
  const [mainTab, setMainTab] = useState(() => localStorage.getItem("scrap_tab") || "records");
  const [records, setRecords] = useState([]);
  const [tools, setTools] = useState([]);
  const [fluids, setFluids] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [toolsPage, setToolsPage] = useState(1);
  const [toolsPageSize, setToolsPageSize] = useState(25);
  const [fluidsPage, setFluidsPage] = useState(1);
  const [fluidsPageSize, setFluidsPageSize] = useState(25);
  const [defsPage, setDefsPage] = useState(1);
  const [defsPageSize, setDefsPageSize] = useState(25);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterRecovery, setFilterRecovery] = useState("");
  const [showScrapForm, setShowScrapForm] = useState(false);
  const [showToolForm, setShowToolForm] = useState(false);
  const [showFluidForm, setShowFluidForm] = useState(false);
  const [showDefForm, setShowDefForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editDef, setEditDef] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        fetch(`${API}/scrap/records`, { headers: hdr() }),
        fetch(`${API}/scrap/tools`, { headers: hdr() }),
        fetch(`${API}/scrap/fluids`, { headers: hdr() }),
        fetch(`${API}/scrap/definitions`, { headers: hdr() }),
        fetch(`${API}/scrap/material-types`, { headers: hdr() }),
        fetch(`${API}/scrap/summary-v2`, { headers: hdr() }),
      ]);
      if (r1.ok) setRecords(await r1.json());
      if (r2.ok) setTools(await r2.json());
      if (r3.ok) setFluids(await r3.json());
      if (r4.ok) setDefinitions(await r4.json());
      if (r5.ok) setMaterialTypes(await r5.json());
      if (r6.ok) setSummary(await r6.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { localStorage.setItem("scrap_tab", mainTab); }, [mainTab]);

  useEffect(() => {
    setPage(1);
    setToolsPage(1);
    setFluidsPage(1);
    setDefsPage(1);
  }, [search, filterStatus, filterRecovery, mainTab]);

  const handleDeleteRecord = async (id) => { if (!window.confirm(s.confirmDelete)) return; await fetch(`${API}/scrap/records/${id}`, { method: "DELETE", headers: hdr() }); fetchData(); };
  const handleDeleteTool   = async (id) => { if (!window.confirm(s.confirmDelete)) return; await fetch(`${API}/scrap/tools/${id}`,   { method: "DELETE", headers: hdr() }); fetchData(); };
  const handleDeleteFluid  = async (id) => { if (!window.confirm(s.confirmDelete)) return; await fetch(`${API}/scrap/fluids/${id}`,  { method: "DELETE", headers: hdr() }); fetchData(); };
  const handleDeleteDef    = async (id) => { if (!window.confirm(s.confirmDeleteDef)) return; await fetch(`${API}/scrap/definitions/${id}`, { method: "DELETE", headers: hdr() }); fetchData(); };

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    return (!q || r.product_code?.toLowerCase().includes(q) || r.work_order_no?.toLowerCase().includes(q) || r.material_name?.toLowerCase().includes(q))
      && (!filterStatus || r.status === filterStatus)
      && (!filterRecovery || r.recovery_method === filterRecovery);
  });

  const pagedRecords = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const sumData = summary?.summary || {};
  const RECOVERY_LABELS = getRecoveryLabels();
  const STATUS_LABELS   = getStatusLabels();
  const TOOL_TYPES      = getToolTypes();
  const FLUID_TYPES     = getFluidTypes();
  const MAT_CAT_LABELS  = getMatCatLabels();

  const pagedTools = tools.slice(
    (toolsPage - 1) * toolsPageSize,
    toolsPage * toolsPageSize
  );

  const pagedFluids = fluids.slice(
    (fluidsPage - 1) * fluidsPageSize,
    fluidsPage * fluidsPageSize
  );

  const pagedDefinitions = definitions.slice(
    (defsPage - 1) * defsPageSize,
    defsPage * defsPageSize
  );

  const tabs = [
    ["records",     `🗑️ ${s.tabRecords}`],
    ["tools",       `🔧 ${s.tabTools}`],
    ["fluids",      `🛢️ ${s.tabFluids}`],
    ["definitions", `⚙️ ${s.tabDefinitions}`],
    ["analytics",   `📊 ${s.tabAnalytics}`],
  ];

  const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" };
  const tdBg = (i) => ({ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row });

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); @keyframes sf-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>♻️ {s.title}</h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>{s.subtitle}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>
            {mainTab === "records"     && <button onClick={() => { setEditRecord(null); setShowScrapForm(true); }} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#f97316,#ef4444)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>➕ {s.addScrap}</button>}
            {mainTab === "tools"       && <button onClick={() => setShowToolForm(true)}                           style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#f97316,#ef4444)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>➕ {s.addTool}</button>}
            {mainTab === "fluids"      && <button onClick={() => setShowFluidForm(true)}                          style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#fbbf24,#f97316)", color: "#fff",  cursor: "pointer", fontSize: 13, fontWeight: 700 }}>➕ {s.addFluid}</button>}
            {mainTab === "definitions" && <button onClick={() => { setEditDef(null); setShowDefForm(true); }}     style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#60a5fa,#a78bfa)", color: "#fff",  cursor: "pointer", fontSize: 13, fontWeight: 700 }}>➕ {s.addDef}</button>}
          </div>
        </div>

        {/* KPI */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 24 }}>
          <KpiCard label={s.kpiWeight} value={kg(sumData.total_weight_kg)}         icon="⚖️" color="#f97316" t={t} />
          <KpiCard label={s.kpiValue}         value={money(sumData.total_scrap_value)}    icon="💸" color="#ef4444" t={t} />
          <KpiCard label={s.kpiRecovery}         value={money(sumData.total_recovery_amount)} icon="💚" color="#22c55e" t={t} />
          <KpiCard label={s.kpiPending}       value={sumData.pending_count || 0}          icon="⏳" color="#eab308" sub={`${sumData.auto_count || 0} ${s.kpiAuto}`} t={t} />
          <KpiCard label={s.kpiToolFluid}          value={`${summary?.tools?.cnt || 0} + ${summary?.fluids?.cnt || 0}`} icon="🔧" color="#a78bfa" sub={money((summary?.tools?.val || 0) + (summary?.fluids?.val || 0))} t={t} />
        </div>

        {/* Sekmeler */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 12, padding: 4, width: "fit-content" }}>
          {tabs.map(([v, label]) => (
            <button key={v} onClick={() => setMainTab(v)} style={{ padding: "9px 16px", borderRadius: 9, border: "none", cursor: "pointer", background: mainTab === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none", color: mainTab === v ? t.text.primary : t.text.muted, fontSize: 13, fontWeight: 600, boxShadow: mainTab === v ? t.shadow.card : "none", transition: "all 0.2s" }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: "#f97316", borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{s.loading}</span>
          </div>
        ) : (
          <>
            {/* HURDALAR */}
            {mainTab === "records" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`🔍 ${s.searchPlaceholder}`}
                    style={{ flex: 1, minWidth: 200, padding: "9px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none" }} />
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, cursor: "pointer" }}>
                    <option value="">{s.allStatuses}</option>
                    {Object.entries(STATUS_LABELS).map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                  </select>
                  <select value={filterRecovery} onChange={e => setFilterRecovery(e.target.value)} style={{ padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, cursor: "pointer" }}>
                    <option value="">{s.allMethods}</option>
                    {Object.entries(RECOVERY_LABELS).map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                  </select>
                </div>
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{s.scrapRecords}</div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "#f9731622", color: "#f97316", fontWeight: 700 }}>🤖 {filtered.filter(r => r.auto_generated).length} {s.kpiAuto}</span>
                      <span style={{ fontSize: 12, color: t.text.muted }}>{filtered.length} {s.records_}</span>
                    </div>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
                      <thead><tr style={{ background: t.bg.theadRow }}>
                        {[s.date, s.product, s.workOrderNo, s.material, s.weight, "₺/kg", s.value, s.recovery, s.recoveryAmount, s.statusLabel, ""].map(h => <th key={h} style={thStyle}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {filtered.length === 0 ? (
                          <tr><td colSpan={11} style={{ padding: 48, textAlign: "center" }}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>♻️</div>
                            <div style={{ color: t.text.placeholder, fontSize: 14 }}>{s.noRecords}</div>
                            <button onClick={() => setShowScrapForm(true)} style={{ marginTop: 12, padding: "8px 18px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#f97316,#ef4444)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ {s.addFirst}</button>
                          </td></tr>
                        ) : pagedRecords.map((r, i) => (
                          <tr key={r.id} style={tdBg(i)}>
                            <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted, whiteSpace: "nowrap" }}>{fmt(r.scrap_date)}{r.auto_generated ? <span style={{ marginLeft: 4, fontSize: 10, color: "#f97316" }}>🤖</span> : null}</td>
                            <td style={{ padding: "10px 12px" }}><div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{r.product_code || "—"}</div><div style={{ fontSize: 10, color: t.text.muted }}>{r.source_type}</div></td>
                            <td style={{ padding: "10px 12px", fontSize: 11, color: t.accent.blue }}>{r.work_order_no || "—"}</td>
                            <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{r.material_name || r.material_category || "—"}</td>
                            <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#f97316" }}>{kg(r.weight_kg)}</td>
                            <td style={{ padding: "10px 12px", fontSize: 11, color: t.text.muted }}>{r.unit_price_per_kg ? `${r.unit_price_per_kg} ₺` : "—"}</td>
                            <td style={{ padding: "10px 12px", fontSize: 12, color: "#ef4444", fontWeight: 600 }}>{money(r.scrap_value)}</td>
                            <td style={{ padding: "10px 12px" }}><Badge label={RECOVERY_LABELS[r.recovery_method] || r.recovery_method} color={RECOVERY_COLORS[r.recovery_method] || "#6b7280"} /></td>
                            <td style={{ padding: "10px 12px", fontSize: 12, color: "#22c55e", fontWeight: 600 }}>{money(r.recovery_amount)}</td>
                            <td style={{ padding: "10px 12px" }}><Badge label={STATUS_LABELS[r.status] || r.status} color={STATUS_COLORS[r.status] || "#6b7280"} /></td>
                            <td style={{ padding: "10px 12px" }}>
                              <div style={{ display: "flex", gap: 6 }}>
                                <button onClick={() => { setEditRecord(r); setShowScrapForm(true); }} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 11 }}>✏️</button>
                                <button onClick={() => handleDeleteRecord(r.id)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 11 }}>🗑️</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* TAKIM */}
            {mainTab === "records" && filtered.length > 0 && (
              <PaginationBar
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalRecords={filtered.length}
                label={s.paginationScrapRecord}
                t={t}
              />
            )}

            {mainTab === "tools" && (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>🔧 {s.toolScrapTitle}</div>
                  <span style={{ fontSize: 12, color: t.text.muted }}>{tools.length} {s.records_}</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr style={{ background: t.bg.theadRow }}>{[s.toolName, s.toolType, s.machine_, s.date, s.quantity_, s.unitPrice, s.total, s.method, ""].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                  <tbody>
                    {tools.length === 0 ? <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", color: t.text.placeholder }}>{s.noTools}</td></tr>
                      : pagedTools.map((r, i) => (
                        <tr key={r.id} style={tdBg(i)}>
                          <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{r.tool_name}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{TOOL_TYPES[r.tool_type] || r.tool_type}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{r.machine_name || "—"}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{fmt(r.scrap_date)}</td>
                          <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#f97316" }}>{r.quantity} {s.pcs}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{money(r.unit_price)}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: "#ef4444", fontWeight: 600 }}>{money(r.total_value)}</td>
                          <td style={{ padding: "10px 12px" }}><Badge label={r.disposal_method === "hurdaci_sat" ? (s.recHurdaciSat) : r.disposal_method === "imha" ? (s.recImha) : (s.recBeklemede)} color={r.disposal_method === "hurdaci_sat" ? "#22c55e" : r.disposal_method === "imha" ? "#ef4444" : "#f97316"} /></td>
                          <td style={{ padding: "10px 12px" }}><button onClick={() => handleDeleteTool(r.id)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 11 }}>🗑️</button></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* SIVI */}
            {mainTab === "tools" && tools.length > 0 && (
              <PaginationBar
                page={toolsPage}
                setPage={setToolsPage}
                pageSize={toolsPageSize}
                setPageSize={setToolsPageSize}
                totalRecords={tools.length}
                label={s.paginationToolScrap}
                t={t}
              />
            )}

            {mainTab === "fluids" && (
              <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>🛢️ {s.fluidScrapTitle}</div>
                  <span style={{ fontSize: 12, color: t.text.muted }}>{fluids.length} {s.records_}</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr style={{ background: t.bg.theadRow }}>{[s.fluidType, s.machine_, s.volumeLt, s.containerType, s.company, s.collectionDate, s.salePrice, s.statusLabel, ""].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                  <tbody>
                    {fluids.length === 0 ? <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", color: t.text.placeholder }}>{s.noFluids}</td></tr>
                      : pagedFluids.map((r, i) => (
                        <tr key={r.id} style={tdBg(i)}>
                          <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{FLUID_TYPES[r.fluid_type] || r.fluid_type}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{r.machine_name || "—"}</td>
                          <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#fbbf24" }}>{r.volume_lt} lt</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{r.container_count} {r.container_type}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{r.collection_company || "—"}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{fmt(r.collection_date)}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: "#22c55e", fontWeight: 600 }}>{money(r.sale_price)}</td>
                          <td style={{ padding: "10px 12px" }}><Badge label={r.status === "satildi" ? (s.statusSatildi) : r.status === "toplandi" ? (s.statusToplandi) : (s.statusBeklemede)} color={r.status === "satildi" ? "#22c55e" : r.status === "toplandi" ? "#60a5fa" : "#f97316"} /></td>
                          <td style={{ padding: "10px 12px" }}><button onClick={() => handleDeleteFluid(r.id)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 11 }}>🗑️</button></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* TANIMLAR */}
            {mainTab === "fluids" && fluids.length > 0 && (
              <PaginationBar
                page={fluidsPage}
                setPage={setFluidsPage}
                pageSize={fluidsPageSize}
                setPageSize={setFluidsPageSize}
                totalRecords={fluids.length}
                label={s.paginationFluidWaste}
                t={t}
              />
            )}

            {mainTab === "definitions" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ padding: "14px 20px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 12, fontSize: 13, color: t.accent.blue }}>
                  💡 {s.defInfo}
                </div>
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
                  <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>⚙️ {s.defTitle}</div>
                    <span style={{ fontSize: 12, color: t.text.muted }}>{definitions.length} {s.defCount}</span>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr style={{ background: t.bg.theadRow }}>{[s.productCode, s.material, s.scrapPerUnit, s.rejectWeight, "₺/kg", s.notes, ""].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                    <tbody>
                      {definitions.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: 48, textAlign: "center" }}>
                          <div style={{ fontSize: 32, marginBottom: 8 }}>⚙️</div>
                          <div style={{ color: t.text.placeholder, fontSize: 14, marginBottom: 12 }}>{s.noDefs}</div>
                          <button onClick={() => { setEditDef(null); setShowDefForm(true); }} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#60a5fa,#a78bfa)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>+ {s.addDef}</button>
                        </td></tr>
                      ) : pagedDefinitions.map((r, i) => (
                        <tr key={r.id} style={tdBg(i)}>
                          <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{r.product_code}</td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.secondary }}>{r.material_name || "—"}</td>
                          <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 13, fontWeight: 700, color: "#22c55e" }}>{r.scrap_per_unit_kg} kg</span><span style={{ fontSize: 10, color: t.text.muted, marginLeft: 4 }}>/ {s.pcs}</span></td>
                          <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>{r.reject_weight_kg} kg</span><span style={{ fontSize: 10, color: t.text.muted, marginLeft: 4 }}>/ {s.reject}</span></td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: t.text.muted }}>{r.avg_price_per_unit ? `${r.avg_price_per_unit} ₺` : "—"}</td>
                          <td style={{ padding: "10px 12px", fontSize: 11, color: t.text.muted, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.notes || "—"}</td>
                          <td style={{ padding: "10px 12px" }}>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button onClick={() => { setEditDef(r); setShowDefForm(true); }} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 11 }}>✏️</button>
                              <button onClick={() => handleDeleteDef(r.id)} style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 11 }}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ANALİZ */}
            {mainTab === "definitions" && definitions.length > 0 && (
              <PaginationBar
                page={defsPage}
                setPage={setDefsPage}
                pageSize={defsPageSize}
                setPageSize={setDefsPageSize}
                totalRecords={definitions.length}
                label={s.paginationScrapDefinition}
                t={t}
              />
            )}

            {mainTab === "analytics" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>🔩 {s.analyticsMaterialCat}</div>
                    {(summary?.by_material || []).length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={summary.by_material} dataKey="weight" nameKey="material_category" cx="50%" cy="50%" outerRadius={70} label={false}>
                              {summary.by_material.map((_, i) => <Cell key={i} fill={MAT_CAT_COLORS[i % MAT_CAT_COLORS.length]} />)}
                            </Pie>
                            <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} formatter={(v) => [`${parseFloat(v).toFixed(2)} kg`]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          {summary.by_material.map((item, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: MAT_CAT_COLORS[i % MAT_CAT_COLORS.length] }} /><span style={{ color: t.text.secondary }}>{MAT_CAT_LABELS[item.material_category] || item.material_category || (s.catOther)}</span></div>
                              <span style={{ fontWeight: 700, color: t.text.primary }}>{parseFloat(item.weight || 0).toFixed(2)} kg · {money(item.value)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{s.noData}</div>}
                  </div>

                  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>♻️ {s.analyticsRecoveryMethod}</div>
                    {(summary?.by_recovery || []).length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={summary.by_recovery} dataKey="weight" nameKey="recovery_method" cx="50%" cy="50%" outerRadius={70} label={false}>
                              {summary.by_recovery.map((entry, i) => <Cell key={i} fill={RECOVERY_COLORS[entry.recovery_method] || MAT_CAT_COLORS[i]} />)}
                            </Pie>
                            <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} formatter={(v, n, p) => [`${parseFloat(v).toFixed(2)} kg`, RECOVERY_LABELS[p.payload.recovery_method] || p.payload.recovery_method]} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          {summary.by_recovery.map((item, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: RECOVERY_COLORS[item.recovery_method] || MAT_CAT_COLORS[i] }} /><span style={{ color: t.text.secondary }}>{RECOVERY_LABELS[item.recovery_method] || item.recovery_method}</span></div>
                              <span style={{ fontWeight: 700, color: t.text.primary }}>{parseFloat(item.weight || 0).toFixed(2)} kg · {money(item.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{s.noData}</div>}
                  </div>
                </div>

                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>📈 {s.analyticsDailyTrend}</div>
                  {(summary?.trend || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={summary.trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                        <XAxis dataKey="date" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} tickFormatter={v => v?.slice(5)} />
                        <YAxis yAxisId="left" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                        <YAxis yAxisId="right" orientation="right" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                        <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="weight" fill="#f97316" radius={[3,3,0,0]} name={s.weightKg} />
                        <Bar yAxisId="right" dataKey="value" fill="#ef444466" radius={[3,3,0,0]} name={s.value} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{s.noTrendData}</div>}
                </div>

                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 24, boxShadow: t.shadow.card }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>📦 {s.analyticsTopProducts}</div>
                  {(summary?.by_product || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={summary.by_product} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                        <XAxis type="number" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} />
                        <YAxis type="category" dataKey="product_code" stroke={t.chart.axis} tick={{ fontSize: 10, fill: t.chart.axis }} width={80} />
                        <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} formatter={(v) => [`${parseFloat(v).toFixed(2)} kg`]} />
                        <Bar dataKey="weight" fill="#60a5fa" radius={[0,3,3,0]} name={s.weightKg} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div style={{ textAlign: "center", color: t.text.placeholder, padding: "60px 0" }}>{s.noData}</div>}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
                  {[
                    { label: s.totalScrapValue, value: money(sumData.total_scrap_value),     icon: "💸", color: "#ef4444" },
                    { label: s.totalRecovery, value: money(sumData.total_recovery_amount), icon: "💚", color: "#22c55e" },
                    { label: s.netLoss,           value: money((sumData.total_scrap_value || 0) - (sumData.total_recovery_amount || 0)), icon: "📉", color: "#f97316" },
                  ].map(card => (
                    <div key={card.label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "20px 24px", boxShadow: t.shadow.card, textAlign: "center" }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{card.icon}</div>
                      <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{card.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>{card.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showScrapForm && <ScrapFormModal record={editRecord} materialTypes={materialTypes} t={t} onClose={() => { setShowScrapForm(false); setEditRecord(null); }} onSaved={() => { setShowScrapForm(false); setEditRecord(null); fetchData(); }} />}
      {showToolForm  && <ToolFormModal  t={t} onClose={() => setShowToolForm(false)}  onSaved={() => { setShowToolForm(false);  fetchData(); }} />}
      {showFluidForm && <FluidFormModal t={t} onClose={() => setShowFluidForm(false)} onSaved={() => { setShowFluidForm(false); fetchData(); }} />}
      {showDefForm   && <DefinitionFormModal record={editDef} materialTypes={materialTypes} t={t} onClose={() => { setShowDefForm(false); setEditDef(null); }} onSaved={() => { setShowDefForm(false); setEditDef(null); fetchData(); }} />}
    </div>
  );
}
